import { useParams } from "react-router-dom";
import { useState, useEffect } from "react";
import _, { isEqual } from "lodash";
import {
  getOrganizationProfessions,
  createProfession,
  updateProfession,
  deleteProfession as deleteProfessionService
} from "../../../service";
import { PlusIcon, RemoveIcon } from "../../../asssets/icons";
import { Spinner } from "../../form";
import { InputSelect, Button } from "../../form";
import { TrashIcon } from "@heroicons/react/24/outline";
import { toast } from "react-toastify";
import { BaseModal } from "../../popups";
import { FaPlus, FaTrash } from "react-icons/fa";

const AddOrgProfessions = ({ org }) => {
  const [originalProfessions, setOriginalProfessions] = useState([]);
  const [professionSection, setProfessionSection] = useState([
    {
      _id: "",
      jobRole: "",
      jobRole_fr: "",
      jobRole_en: "",
      jobRole_de: "",
      jobRole_it: "",
      url: "",
      url_en: "",
      url_de: "",
      url_it: "",
      url_fr: "",
      alternateUrls: {},
      female: {
        correctAnswers: "",
        answers: []
      },
      male: {
        correctAnswers: "",
        answers: []
      },
      female_fr: {
        correctAnswers: "",
        answers: []
      },
      male_fr: {
        correctAnswers: "",
        answers: []
      },
      female_de: {
        correctAnswers: "",
        answers: []
      },
      male_de: {
        correctAnswers: "",
        answers: []
      },
      female_it: {
        correctAnswers: "",
        answers: []
      },
      male_it: {
        correctAnswers: "",
        answers: []
      },
      female_en: {
        correctAnswers: "",
        answers: []
      },
      male_en: {
        correctAnswers: "",
        answers: []
      },
      type: "",
      accessibleTo: [],
      description_fr: "",
      description_en: "",
      description_de: "",
      description_it: "",
      domeText_fr: "",
      domeText_en: "",
      domeText_de: "",
      domeText_it: ""
    }
  ]);
  const [accessibleToOptions, setAccessibleToOptions] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [deleteProfession, setDeleteProfession] = useState(null);

  const { orgId } = useParams();

  const fetchProfessions = async () => {
    try {
      setLoading(true);
      const { data } = await getOrganizationProfessions(orgId);
      data.length > 0 ? setProfessionSection(data) : addProfessionSection();
      setOriginalProfessions(data);

      const newAccessibleToOptions = data
        .map((p) => p.accessibleTo)
        .flat()
        .filter(Boolean);
      const uniqueAccessibleToOptions = [...new Set(newAccessibleToOptions)];
      setAccessibleToOptions(
        uniqueAccessibleToOptions.map((option) => ({
          value: option,
          label: option
        }))
      );
    } catch (error) {
      setOriginalProfessions([]);
      console.log(error);
      toast.error("Error fetching professions");
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchProfessions(orgId);
  }, [orgId, org]);

  const addProfessionSection = () => {
    setProfessionSection([
      ...professionSection,
      {
        _id: "",
        jobRole: "",
        jobRole_fr: "",
        jobRole_en: "",
        jobRole_de: "",
        jobRole_it: "",
        url: "",
        url_en: "",
        url_de: "",
        url_it: "",
        url_fr: "",
        alternateUrls: {},
        female: {
          correctAnswers: "",
          answers: []
        },
        male: {
          correctAnswers: "",
          answers: []
        },
        female_fr: {
          correctAnswers: "",
          answers: []
        },
        male_fr: {
          correctAnswers: "",
          answers: []
        },
        female_de: {
          correctAnswers: "",
          answers: []
        },
        male_de: {
          correctAnswers: "",
          answers: []
        },
        female_it: {
          correctAnswers: "",
          answers: []
        },
        male_it: {
          correctAnswers: "",
          answers: []
        },
        female_en: {
          correctAnswers: "",
          answers: []
        },
        male_en: {
          correctAnswers: "",
          answers: []
        },
        type: "",
        accessibleTo: [],
        description_fr: "",
        description_en: "",
        description_de: "",
        description_it: "",
        domeText_fr: "",
        domeText_en: "",
        domeText_de: "",
        domeText_it: ""
      }
    ]);
  };

  const handleDeleteClick = (section) => {
    const professionToDelete = section;
    const professionName =
      professionToDelete.jobRole_en ||
      professionToDelete.jobRole_fr ||
      professionToDelete.jobRole_de ||
      professionToDelete.jobRole_it ||
      professionToDelete.jobRole ||
      `Profession #${professionSection.indexOf(section) + 1}`;

    setDeleteProfession({ ...professionToDelete, title: professionName });
  };

  const confirmDeleteProfession = async (deleteData) => {
    const { _id } = deleteData;
    let updatedSections = [...professionSection];

    try {
      if (_id !== "") {
        await deleteProfessionService(orgId, _id);
        toast.success("Profession deleted successfully");
      }
      updatedSections = updatedSections.filter((section) => section._id !== _id);
      setProfessionSection(updatedSections);
    } catch (error) {
      console.log(error);
      toast.error("Error deleting profession", error);
    }

    setDeleteProfession(null);
  };

  const handleChange = (index, path, value, callback) => {
    const updatedSections = _.cloneDeep([...professionSection]);
    updatedSections[index] = _.set(updatedSections[index], path, value);
    if (callback) {
      callback(updatedSections);
    }
    setProfessionSection(updatedSections);
  };

  const handleAccessibleToChange = (index, selectedOptions) => {
    const currentAccessibleTo = professionSection[index].accessibleTo || [];
    const values = selectedOptions ? selectedOptions.map((option) => option.value) : [];
    const removedAccessibleTo = currentAccessibleTo.filter((option) => !values.includes(option));
    handleChange(index, "accessibleTo", values, (updatedSections) => {
      removedAccessibleTo.forEach((option) => {
        updatedSections[index] = _.set(
          updatedSections[index],
          `alternateUrls.${option}`,
          undefined
        );
      });
    });
  };

  const addAnswerField = (index, gender, language = "") => {
    const fieldName = language ? `${gender}_${language}` : gender;
    const currentAnswers = professionSection[index][fieldName]?.answers || [];
    handleChange(index, `${fieldName}.answers`, [...currentAnswers, ""]);
  };

  const updateAnswerField = (index, gender, answerIndex, value, language = "") => {
    const fieldName = language ? `${gender}_${language}` : gender;
    const currentAnswers = [...professionSection[index][fieldName].answers];
    currentAnswers[answerIndex] = value;
    handleChange(index, `${fieldName}.answers`, currentAnswers);
  };

  const removeAnswerField = (index, gender, answerIndex, language = "") => {
    const fieldName = language ? `${gender}_${language}` : gender;
    const currentAnswers = professionSection[index][fieldName].answers.filter(
      (_, i) => i !== answerIndex
    );
    handleChange(index, `${fieldName}.answers`, currentAnswers);
  };

  const saveProfession = async () => {
    setSaving(true);
    for (const [index, item] of professionSection.entries()) {
      if (item._id !== "") {
        if (!isEqual(item, originalProfessions[index])) {
          setSaving(`Updating profession #${index + 1}...`);
          await updateProfession(orgId, item._id, item);
        }
      } else {
        setSaving(`Adding profession #${index + 1}...`);
        await createProfession(item, orgId);
      }
    }

    setSaving("Professions saved successfully");
    fetchProfessions(orgId);
    setTimeout(() => {
      setSaving(false);
    }, 1000);
  };

  const addAlternateUrl = (index, accessibleOption) => {
    const currentAlternateUrls = professionSection[index].alternateUrls || {};
    if (!currentAlternateUrls[accessibleOption]) {
      const newAlternateUrls = {
        ...currentAlternateUrls,
        [accessibleOption]: {}
      };
      org.languages?.forEach((lang) => {
        newAlternateUrls[accessibleOption][`url_${lang}`] = "";
      });
      handleChange(index, "alternateUrls", newAlternateUrls);
    }
  };

  const removeAlternateUrl = (index, accessibleOption) => {
    const currentAlternateUrls = { ...professionSection[index].alternateUrls };
    delete currentAlternateUrls[accessibleOption];
    handleChange(index, "alternateUrls", currentAlternateUrls);
  };

  const handleAddAccessibleToOption = (e) => {
    e.preventDefault();
    const newAccessibleToOption = e.target.accessibleToOption.value;
    setAccessibleToOptions([
      ...accessibleToOptions,
      {
        value: newAccessibleToOption?.trim(),
        label: newAccessibleToOption?.trim()
      }
    ]);
  };

  if (loading)
    return (
      <div className="text-center">
        <Spinner size={16} color="#515151" isSubmitting={loading} />
      </div>
    );

  return (
    <>
      <div className="container mx-auto p-4">
        <div className="flex justify-end mt-4 fixed top-4 right-4 z-50">
          <button
            onClick={saveProfession}
            type="button"
            disabled={saving}
            className="flex flex-col gap-2 disabled:opacity-60 select-none py-2 font-bold text-md border bg-[#515151] text-[#FFFFFF] px-8 rounded-3xl">
            <span className="flex items-center gap-2">
              Save Professions{" "}
              {saving && <Spinner isSubmitting={saving} size={16} color="#FFFFFF" />}
            </span>
            {saving && (
              <span className="block">
                <span className="text-xs text-gray-300">
                  {typeof saving === "string" ? saving : "Saving..."}
                </span>
              </span>
            )}
          </button>
        </div>
      </div>
      <div className="container mx-auto mb-4 bg-gray-100 p-4 rounded-lg">
        <h3 className="text-lg font-semibold text-gray-800 mb-2">Accessible To Options</h3>
        <div className="grid grid-cols-2 gap-4">
          {accessibleToOptions.map((aOption, index) => {
            const canBeDeleted = professionSection.some((p) =>
              p.accessibleTo.includes(aOption.value)
            );
            return (
              <div
                key={index}
                className="flex w-full items-center justify-between border border-gray-200 rounded-lg p-4 bg-white">
                <span>{aOption.label}</span>
                <button
                  disabled={canBeDeleted}
                  className="text-red-500 hover:bg-red-50 px-2 py-1 rounded text-xs disabled:opacity-50 disabled:cursor-not-allowed">
                  <FaTrash className="w-3 h-3" />
                </button>
              </div>
            );
          })}
        </div>
        <form onSubmit={handleAddAccessibleToOption} className="flex gap-2 mt-2">
          <input
            type="text"
            name="accessibleToOption"
            placeholder="Enter accessible to option"
            className="w-3/5 px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 bg-gray-50 text-sm focus:border-black focus:ring-0 focus:outline-none transition-colors"
          />
          <button
            type="submit"
            className="flex items-center gap-2 bg-blue-500 text-white hover:scale-105 transition-all duration-300 px-2 py-1 rounded text-xs">
            <FaPlus className="w-3 h-3" />
            Add new Accessible To Option
          </button>
        </form>
      </div>
      <div className="space-y-8">
        {professionSection.map((section, index) => {
          return (
            <div key={index} className="container mx-auto">
              <div className="bg-white border-2 border-gray-200 rounded-lg p-6 shadow-sm">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-lg font-semibold text-gray-800">Profession #{index + 1}</h3>
                  <button
                    className="p-2 text-red-500 hover:bg-red-50 rounded-full transition-colors"
                    onClick={() => handleDeleteClick(section)}>
                    <RemoveIcon />
                  </button>
                </div>

                <div className="grid grid-cols-1 gap-6">
                  {/* Basic Information */}
                  <div className="space-y-4">
                    <h4 className="text-md font-medium text-gray-700 border-b pb-2">
                      Basic Information
                    </h4>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label
                          htmlFor={`${section._id}_type`}
                          className="block text-sm font-medium text-gray-600 mb-1">
                          Type
                        </label>
                        <input
                          id={`${section._id}_type`}
                          type="text"
                          value={section.type || ""}
                          onChange={(e) => handleChange(index, "type", e.target.value)}
                          placeholder="Enter profession type"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 bg-gray-50 text-sm focus:border-black focus:ring-0 focus:outline-none transition-colors"
                        />
                      </div>

                      <div>
                        <label
                          htmlFor={`${section._id}_accessible_to`}
                          className="block text-sm font-medium text-gray-600 mb-1">
                          Accessible To
                        </label>
                        <InputSelect
                          id={`${section._id}_accessible_to`}
                          isMulti={true}
                          borderRadius="6px"
                          options={accessibleToOptions}
                          value={accessibleToOptions.filter((option) =>
                            section.accessibleTo?.includes(option.value)
                          )}
                          onChange={(selectedOptions) =>
                            handleAccessibleToChange(index, selectedOptions)
                          }
                          placeholder="Select accessible options..."
                        />
                      </div>
                    </div>
                  </div>

                  {/* Language Fields */}
                  <div className="space-y-6">
                    <h4 className="text-md font-medium text-gray-700 border-b pb-2">
                      Language Content
                    </h4>

                    {org.languages?.map((language, languageIndex) => (
                      <div
                        key={languageIndex}
                        className="space-y-4 border border-gray-200 rounded-lg p-4">
                        <h5 className="text-sm font-medium text-gray-700 uppercase tracking-wide bg-gray-100 px-2 py-1 rounded">
                          {language}
                        </h5>

                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label
                              htmlFor={`${section._id}_job_role_${language}`}
                              className="block text-xs font-medium text-gray-600 mb-1">
                              Job Role
                            </label>
                            <input
                              type="text"
                              id={`${section._id}_job_role_${language}`}
                              value={section[`jobRole_${language}`] || ""}
                              onChange={(e) =>
                                handleChange(index, `jobRole_${language}`, e.target.value)
                              }
                              placeholder={`Enter job role in ${language.toUpperCase()}`}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 bg-gray-50 text-sm focus:border-black focus:ring-0 focus:outline-none transition-colors"
                            />
                          </div>

                          <div>
                            <label
                              htmlFor={`${section._id}_url_${language}`}
                              className="block text-xs font-medium text-gray-600 mb-1">
                              URL
                            </label>
                            <input
                              type="url"
                              id={`${section._id}_url_${language}`}
                              value={section[`url_${language}`] || ""}
                              onChange={(e) =>
                                handleChange(index, `url_${language}`, e.target.value)
                              }
                              placeholder={`Enter URL in ${language.toUpperCase()}`}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 bg-gray-50 text-sm focus:border-black focus:ring-0 focus:outline-none transition-colors"
                            />
                          </div>
                        </div>

                        <div>
                          <label
                            htmlFor={`${section._id}_description_${language}`}
                            className="block text-xs font-medium text-gray-600 mb-1">
                            Description
                          </label>
                          <textarea
                            id={`${section._id}_description_${language}`}
                            value={section[`description_${language}`] || ""}
                            onChange={(e) =>
                              handleChange(index, `description_${language}`, e.target.value)
                            }
                            placeholder={`Enter description in ${language.toUpperCase()}`}
                            rows={3}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 bg-gray-50 text-sm focus:border-black focus:ring-0 focus:outline-none resize-none transition-colors"
                          />
                        </div>

                        <div>
                          <label
                            htmlFor={`${section._id}_domeText_${language}`}
                            className="block text-xs font-medium text-gray-600 mb-1">
                            Dome Text
                          </label>
                          <input
                            id={`${section._id}_domeText_${language}`}
                            value={section[`domeText_${language}`] || ""}
                            onChange={(e) =>
                              handleChange(index, `domeText_${language}`, e.target.value)
                            }
                            placeholder={`Enter dome text in ${language.toUpperCase()}`}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 bg-gray-50 text-sm focus:border-black focus:ring-0 focus:outline-none resize-none transition-colors"
                          />
                        </div>

                        {/* Gender-based Questions */}
                        <div className="grid grid-cols-2 gap-4">
                          {["female", "male"].map((gender) => (
                            <div key={gender} className="space-y-2">
                              <h6 className="text-xs font-medium text-gray-600 capitalize">
                                {gender} Questions
                              </h6>

                              <div>
                                <label className="block text-xs text-gray-500 mb-1">
                                  Correct Answer
                                </label>
                                <input
                                  type="text"
                                  value={section[`${gender}_${language}`]?.correctAnswers || ""}
                                  onChange={(e) =>
                                    handleChange(
                                      index,
                                      `${gender}_${language}.correctAnswers`,
                                      e.target.value
                                    )
                                  }
                                  placeholder="Correct answer"
                                  className="w-full px-2 py-1 border border-gray-300 rounded text-xs focus:border-black focus:ring-0 focus:outline-none"
                                />
                              </div>

                              <div>
                                <label className="block text-xs text-gray-500 mb-1">
                                  Answer Options
                                </label>
                                {section[`${gender}_${language}`]?.answers?.map(
                                  (answer, answerIndex) => (
                                    <div key={answerIndex} className="flex gap-1 mb-1">
                                      <input
                                        type="text"
                                        value={answer}
                                        onChange={(e) =>
                                          updateAnswerField(
                                            index,
                                            gender,
                                            answerIndex,
                                            e.target.value,
                                            language
                                          )
                                        }
                                        placeholder={`Option ${answerIndex + 1}`}
                                        className="flex-1 px-2 py-1 border border-gray-300 rounded text-xs focus:border-black focus:ring-0 focus:outline-none"
                                      />
                                      <button
                                        onClick={() =>
                                          removeAnswerField(index, gender, answerIndex, language)
                                        }
                                        className="text-red-500 hover:bg-red-50 px-1 rounded">
                                        <TrashIcon className="w-3 h-3" />
                                      </button>
                                    </div>
                                  )
                                )}
                                <button
                                  onClick={() => addAnswerField(index, gender, language)}
                                  className="text-xs text-blue-600 hover:text-blue-800">
                                  + Add Option
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Alternate URLs */}
                  {section.accessibleTo?.length > 0 && (
                    <div className="space-y-4">
                      <h4 className="text-md font-medium text-gray-700 border-b pb-2">
                        Alternate URLs
                      </h4>

                      {section.alternateUrls &&
                        Object.keys(section.alternateUrls)?.map((alternateUrlKey) => {
                          if (!section.accessibleTo?.includes(alternateUrlKey)) return null;

                          return (
                            <div
                              key={alternateUrlKey}
                              className="border border-gray-200 rounded-lg p-4">
                              <div className="flex justify-between items-center mb-3">
                                <h5 className="text-sm font-medium text-gray-700 capitalize">
                                  {alternateUrlKey}
                                </h5>
                                <button
                                  onClick={() => removeAlternateUrl(index, alternateUrlKey)}
                                  className="text-red-500 hover:bg-red-50 px-2 py-1 rounded text-xs">
                                  Remove
                                </button>
                              </div>

                              <div className="grid grid-cols-2 gap-3">
                                {org.languages?.map((lang) => (
                                  <div key={lang}>
                                    <label className="block text-xs text-gray-500 mb-1">
                                      URL ({lang.toUpperCase()})
                                    </label>
                                    <input
                                      type="url"
                                      value={
                                        section.alternateUrls?.[alternateUrlKey]?.[`url_${lang}`] ||
                                        ""
                                      }
                                      onChange={(e) =>
                                        handleChange(
                                          index,
                                          `alternateUrls.${alternateUrlKey}.url_${lang}`,
                                          e.target.value
                                        )
                                      }
                                      placeholder={`Enter URL for ${lang.toUpperCase()}`}
                                      className="w-full px-2 py-1 border border-gray-300 rounded text-xs focus:border-black focus:ring-0 focus:outline-none"
                                    />
                                  </div>
                                ))}
                              </div>
                            </div>
                          );
                        })}

                      {section.accessibleTo?.length > 0 && (
                        <div className="flex gap-2 flex-wrap">
                          {accessibleToOptions
                            .filter(
                              (option) =>
                                section.accessibleTo?.includes(option.value) &&
                                !section.alternateUrls?.[option.value]
                            )
                            .map((option) => (
                              <button
                                key={option.value}
                                onClick={() => addAlternateUrl(index, option.value)}
                                className="text-xs px-3 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200">
                                + Add {option.label} URL
                              </button>
                            ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <div className="p-4 w-full">
        <div className="flex justify-end mt-8 w-full">
          <button
            className="flex items-center gap-2 px-6 py-3 bg-blue-200 text-[#606060] text-xl font-bold rounded-lg hover:bg-blue-400 transition-colors shadow-sm"
            onClick={() => addProfessionSection()}>
            <span className="w-8 h-8">
              <PlusIcon />
            </span>
            <span>Add New Profession</span>
          </button>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <BaseModal
        open={deleteProfession ? true : false}
        setOpen={setDeleteProfession}
        title={`Are you sure you want to delete '${deleteProfession?.title}'`}>
        {deleteProfession && (
          <>
            <p className="text-base text-black">This action cannot be undone</p>
            <Button.ButtonOutlined
              className={
                "border-red-500 flex items-center justify-center w-full text-red-500 gap-2 mt-2"
              }
              handler={() => confirmDeleteProfession(deleteProfession)}>
              <span className="w-4">
                <FaTrash />
              </span>
              Delete
            </Button.ButtonOutlined>
          </>
        )}
      </BaseModal>
    </>
  );
};

export default AddOrgProfessions;
